import type { ReactNode } from "react";
import { Pagination } from "@/shared/components/Pagination";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";

export interface PagedResultsLabels {
  /** Accessible name of the loading skeleton. */
  readonly loading: string;
  readonly retry: string;
  /** The button of the past-the-end empty state. */
  readonly lastPage: string;
}

interface PagedResultsProps {
  /** Prefix of every `data-testid`: `{prefix}-loading|error|retry|empty|last-page|list|pagination`. */
  readonly testIdPrefix: string;
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
  /** Rows on this page. */
  readonly itemCount: number;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
  /** Our own copy for the failure; callers never pass a server message. */
  readonly errorMessage: string;
  /** False for a failure a retry cannot fix (403): the button is not offered. */
  readonly canRetry?: boolean;
  readonly labels: PagedResultsLabels;
  readonly onRetry: () => void;
  readonly onPageChange: (page: number) => void;
  readonly emptyTitle?: string;
  /** Empty-state copy for a list that is genuinely empty. */
  readonly emptyBody: string;
  /** Empty-state copy when rows vanished and an earlier page still has some. */
  readonly pastEndBody: string;
  /** The rows (a table). */
  readonly children: ReactNode;
}

/**
 * Loading, error, empty and list states plus the pager of a paged admin read.
 * Copy is injected, so each list keeps its own wording; the markup and the
 * `data-testid` grammar are shared. The error state never shows a server
 * message (the responses name people and the reads are audited).
 */
export function PagedResults({
  testIdPrefix,
  page,
  limit,
  totalCount,
  itemCount,
  isLoading,
  isFetching,
  isError,
  errorMessage,
  canRetry = true,
  labels,
  onRetry,
  onPageChange,
  emptyTitle,
  emptyBody,
  pastEndBody,
  children,
}: PagedResultsProps) {
  const lastPage = Math.max(1, Math.ceil(totalCount / limit));
  // Rows vanished between loads: this page is empty but earlier ones are not.
  const pastTheEnd = itemCount === 0 && totalCount > 0 && page > lastPage;

  return (
    <>
      {isLoading && (
        <div role="status" aria-label={labels.loading} data-testid={`${testIdPrefix}-loading`}>
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {isError && (
        <div
          role="alert"
          data-testid={`${testIdPrefix}-error`}
          className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">{errorMessage}</p>
          {canRetry && (
            <Button
              data-testid={`${testIdPrefix}-retry`}
              variant="outline"
              size="sm"
              onClick={onRetry}
            >
              {labels.retry}
            </Button>
          )}
        </div>
      )}

      {!isLoading && !isError && itemCount === 0 && (
        <div
          data-testid={`${testIdPrefix}-empty`}
          role="status"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          {emptyTitle && (
            <p className="font-sans text-sm font-medium text-foreground">{emptyTitle}</p>
          )}
          <p
            className={
              emptyTitle
                ? "mt-1 font-sans text-sm font-normal text-muted-foreground"
                : "font-sans text-sm font-normal text-muted-foreground"
            }
          >
            {pastTheEnd ? pastEndBody : emptyBody}
          </p>
          {pastTheEnd && (
            <Button
              data-testid={`${testIdPrefix}-last-page`}
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => onPageChange(lastPage)}
            >
              {labels.lastPage}
            </Button>
          )}
        </div>
      )}

      {!isError && itemCount > 0 && (
        <div
          data-testid={`${testIdPrefix}-list`}
          aria-busy={isFetching}
          className={isFetching ? "space-y-4 opacity-70 transition-opacity" : "space-y-4"}
        >
          {children}
        </div>
      )}

      {/* Hidden when the page is past the end: the empty state offers the way back. */}
      {!isError && totalCount > 0 && !pastTheEnd && (
        <Pagination
          page={page}
          limit={limit}
          totalCount={totalCount}
          onPageChange={onPageChange}
          testIdPrefix={testIdPrefix}
        />
      )}
    </>
  );
}
